"use client";

import {
  Box,
  Flex,
  Skeleton,
  SkeletonCircle,
  Heading,
  Text,
  Link,
  Spinner,
  Badge,
  useBreakpointValue,
  Tooltip,
} from "@chakra-ui/react";
import Image from "next/image";
import { Tag } from "@chakra-ui/react";
import React, { useState } from "react";
import AppButton from "../ui/Button/AppButton";
import { Business } from "@/app/api/dummy/types";
import { recordBusinessClickApi } from "@/app/api/businessApi";
import useRoleBasedNavigation from "../../utils/rolebasenavigation";
import { sendGAEvent } from "@next/third-parties/google";

interface BusinessCardProps extends Business {
  isAnyClicked?: boolean;
  setIsAnyClicked?: (clicked: boolean) => void;
  slug?: string;
  // categories?: string[];
  promotedBusinessIds?: boolean;
}

const BusinessCard: React.FC<BusinessCardProps> = ({
  id,
  name = "Unnamed Business",
  categories = [],
  description = "No description available.",
  website,
  imageUrl,
  isLoading,
  isAnyClicked,
  setIsAnyClicked,
  slug,
  promotedBusinessIds,
}) => {
  const { navigate } = useRoleBasedNavigation();
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const safeImageUrl =
    typeof imageUrl === "string" && imageUrl.trim() !== ""
      ? imageUrl
      : "/placeholder.png";
  const handleViewDetails = async (businessId: string) => {
    if (isAnyClicked) return;
    setIsAnyClicked?.(true);
    setIsLoadingDetails(true);
    try {
      await recordBusinessClickApi(businessId);
      //  navigate(`viewdetails/${businessId}`);
      navigate(`${slug}`);
    } catch {
      setIsAnyClicked?.(false);
      setIsLoadingDetails(false);
    }
  };

  const avatarSize = useBreakpointValue({ base: "40px", md: "50px" });
  const avatarSkeleton = useBreakpointValue({ base: "10", md: "14" });
  const headingSize = useBreakpointValue({ base: "sm", md: "md" });
  const fontSize = useBreakpointValue({ base: "sm", md: "md" });

  return (
    <>
      <Flex
        direction="column"
        justify="space-between"
        position="relative"
        w="100%"
        maxW={{ base: "100%", sm: "300px", md: "360px" }}
        p={{ base: 3, md: 4 }}
        borderWidth="1px"
        borderRadius="md"
        boxShadow="md"
        fontSize={fontSize}
        cursor={isAnyClicked ? "not-allowed" : "pointer"}
        bg="white"
        minH="250px"
        _hover={{
          boxShadow: "lg",
          transform: "scale(1.02)",
          transition: "all 0.2s ease-in-out",
        }}
        onClick={() => handleViewDetails(id)}
        opacity={isAnyClicked && !isLoadingDetails ? 0.5 : 1}
        pointerEvents={isAnyClicked && !isLoadingDetails ? "none" : "auto"}
      >
        <Box>
          {promotedBusinessIds && (
            <Tag
              position="absolute"
              top="4px"
              right="8px"
              colorScheme="gray"
              fontSize="sm"
              borderRadius="md"
              color="black"
            >
              Promoted
            </Tag>
          )}

          <Flex align="center" gap={{ base: 2, md: 4 }} mb={3}>
            <Box
              boxSize={avatarSize}
              borderRadius="full"
              overflow="hidden"
              position="relative"
              flexShrink={0}
            >
              <SkeletonCircle size={avatarSkeleton} isLoaded={!isLoading} />
              {!isLoading && (
                <Image
                  src={safeImageUrl}
                  alt="Business Logo"
                  fill
                  sizes={`${avatarSize} ${avatarSize}`}
                  style={{ objectFit: "cover" }}
                  loading="eager"
                  priority={true}
                  fetchPriority="high"
                />
              )}
            </Box>

            <Skeleton isLoaded={!isLoading}>
              <Heading
                size={headingSize}
                whiteSpace="normal"
                overflowWrap="break-word"
                wordBreak="break-word"
              >
                {name}
              </Heading>
            </Skeleton>
          </Flex>

          <Skeleton isLoaded={!isLoading}>
            <Flex wrap="wrap" gap={2} mb={1}>
              {categories.length > 0 ? (
                <Tooltip
                  label={categories.map((cat) => cat.categoryname).join(", ")}
                  hasArrow
                  placement="top"
                  fontSize="sm"
                >
                  <Badge
                    colorScheme="blue"
                    variant="subtle"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    cursor="pointer"
                  >
                    {categories.length === 1
                      ? categories[0].categoryname
                      : `${categories[0].categoryname}, ...`}
                  </Badge>
                </Tooltip>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  No categories
                </Text>
              )}
            </Flex>
          </Skeleton>

          <Skeleton isLoaded={!isLoading} noOfLines={1}>
            <Text fontSize="sm" mb={3}>
              {description.length > 60
                ? description.slice(0, 60) + "..."
                : description}
            </Text>
          </Skeleton>

          <Skeleton isLoaded={!isLoading} noOfLines={1}>
            {website && (
              <Link
                href={`https://${website}`}
                target="_blank"
                color="#277BA7"
                fontWeight="medium"
                onClick={(e) => e.stopPropagation()}
              >
                Visit Website
              </Link>
            )}
          </Skeleton>
        </Box>

        <Skeleton isLoaded={!isLoading} width="100%" mt={4}>
          <AppButton
            w="100%"
            size={{ base: "sm", md: "md" }}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(id);
              sendGAEvent("event", "page_view", {
                page_title: `${name}`,
              });
            }}
            isDisabled={isAnyClicked && !isLoadingDetails}
            label={
              isLoadingDetails ? (
                <Flex align="center" justify="center" gap={2}>
                  <Spinner size={{ base: "sm", md: "md" }} /> Loading...
                </Flex>
              ) : (
                "View Details"
              )
            }
          />
        </Skeleton>
      </Flex>
    </>
  );
};

export default BusinessCard;
